import { useState, useRef } from "react"

export function useCameraValidation() {
    const [validationResult, setValidationResult] = useState(null)
    const [isValidating, setIsValidating] = useState(false)
    const videoRef = useRef(null)
    const canvasRef = useRef(null)

    const validateCamera = async (stream) => {
        const reasons = []
        let confidence = 0
        let isVirtual = false

        try {
            const devices = await navigator.mediaDevices.enumerateDevices()
            const videoDevices = devices.filter((device) => device.kind === "videoinput")

            const track = stream.getVideoTracks()[0]
            const settings = track.getSettings()
            const capabilities = track.getCapabilities()

            const currentDevice = videoDevices.find((device) => device.deviceId === settings.deviceId)

            const virtualCameraNames = [
                "obs virtual camera",
                "obs-camera",
                "manycam virtual",
                "xsplit vcam",
                "streamlabs virtual",
                "snap camera",
                "nvidia broadcast camera",
                "mmhmm camera",
                "camo",
                "epoccam",
                "droidcam",
                "iriun webcam",
                "virtual camera",
                "expression-camera",
                "virtual cam",
                "fake camera",
                "screen capture",
                "desktop capture",
            ]

            if (currentDevice?.label) {
                const deviceName = currentDevice.label.toLowerCase()

                // Verificar nombres exactos conocidos
                const hasVirtualName = virtualCameraNames.some((name) => deviceName.includes(name) || deviceName === name)

                // Verificar patrones sospechosos adicionales
                const suspiciousPatterns = [
                    /virtual/i,
                    /fake/i,
                    /screen/i,
                    /capture/i,
                    /broadcast/i,
                    /stream/i,
                    /cam\s*\d+$/i, // Nombres como "cam1", "cam 2", etc.
                    /^camera\s*\d*$/i, // Nombres genéricos como "camera", "camera1"
                ]

                const hasSuspiciousPattern = suspiciousPatterns.some((pattern) => pattern.test(deviceName))

                if (hasVirtualName) {
                    reasons.push(`Dispositivo identificado como virtual: ${currentDevice.label}`)
                    confidence += 70
                    isVirtual = true
                } else if (hasSuspiciousPattern) {
                    reasons.push(`Nombre de dispositivo sospechoso: ${currentDevice.label}`)
                    confidence += 40
                }
            }

            if (capabilities) {
                const realCameraCapabilities = {
                    hasZoom: capabilities.zoom && capabilities.zoom.max > 1,
                    hasFocusMode: capabilities.focusMode && capabilities.focusMode.length > 1,
                    hasWhiteBalance: capabilities.whiteBalanceMode && capabilities.whiteBalanceMode.length > 1,
                    hasTorch: capabilities.torch !== undefined,
                    hasExposureMode: capabilities.exposureMode && capabilities.exposureMode.length > 1,
                    hasIso: capabilities.iso !== undefined,
                    hasColorTemperature: capabilities.colorTemperature !== undefined,
                }

                const capabilityCount = Object.values(realCameraCapabilities).filter(Boolean).length

                // Las cámaras virtuales típicamente tienen muy pocas capacidades avanzadas
                if (capabilityCount === 0) {
                    reasons.push("Sin capacidades avanzadas de cámara física")
                    confidence += 25
                } else if (capabilityCount <= 2) {
                    reasons.push("Capacidades limitadas de cámara física")
                    confidence += 15
                }

                if (settings.width && settings.height) {
                    const resolution = `${settings.width}x${settings.height}`

                    // Resoluciones típicas de contenido pregrabado o streaming
                    const suspiciousResolutions = [
                        "640x360",
                        "854x480",
                        "1024x576", // Resoluciones de streaming bajo
                        "1366x768",
                        "1600x900", // Resoluciones de pantalla comunes
                    ]

                    if (suspiciousResolutions.includes(resolution)) {
                        reasons.push(`Resolución sospechosa: ${resolution}`)
                        confidence += 20
                    }
                }
            }

            if (settings.frameRate) {
                // Framerates típicos de contenido pregrabado o software
                const suspiciousFramerates = [25, 29.97, 23.976, 24, 50, 59.94]

                if (suspiciousFramerates.includes(settings.frameRate)) {
                    reasons.push(`Framerate típico de contenido pregrabado: ${settings.frameRate}fps`)
                    confidence += 25
                }

                // Framerates demasiado perfectos (exactamente 30.0 o 60.0) pueden ser sospechosos
                if (settings.frameRate === 30.0 || settings.frameRate === 60.0) {
                    confidence += 10
                    reasons.push("Framerate perfectamente redondo (posible software)")
                }
            }

            if (currentDevice?.label) {
                const knownManufacturers = [
                    /logitech/i,
                    /microsoft/i,
                    /creative/i,
                    /razer/i,
                    /asus/i,
                    /hp/i,
                    /dell/i,
                    /lenovo/i,
                    /apple/i,
                    /sony/i,
                    /canon/i,
                    /nikon/i,
                    /panasonic/i,
                    /samsung/i,
                    /lg/i,
                    /webcam/i,
                    /usb/i,
                ]

                const hasKnownManufacturer = knownManufacturers.some((pattern) => pattern.test(currentDevice.label))

                if (!hasKnownManufacturer && currentDevice.label.length < 20) {
                    confidence += 20
                    reasons.push("Dispositivo sin fabricante reconocido o nombre muy genérico")
                }
            }

            if (currentDevice?.deviceId) {
                // Los dispositivos virtuales a menudo tienen IDs muy cortos o patrones específicos
                if (currentDevice.deviceId.length < 20) {
                    confidence += 15
                    reasons.push("ID de dispositivo sospechosamente corto")
                }
            }

            return {
                isVirtual: confidence > 50,
                confidence,
                reasons,
                deviceInfo: currentDevice || null,
            }
        } catch (error) {
            console.error("Error validating camera:", error)
            return {
                isVirtual: true,
                confidence: 100,
                reasons: ["Error al acceder a información del dispositivo"],
                deviceInfo: null,
            }
        }
    }

    const analyzeVideoFrames = async (
        stream,
) => {
        return new Promise((resolve) => {
            if (!videoRef.current || !canvasRef.current) {
                resolve({ staticPercentage: 0, additionalSuspicion: 0 })
                return
            }

            const video = videoRef.current
            const canvas = canvasRef.current
            const ctx = canvas.getContext("2d")

            if (!ctx) {
                resolve({ staticPercentage: 0, additionalSuspicion: 0 })
                return
            }

            video.srcObject = stream
            video.play()

            let frameCount = 0
            let staticFrames = 0
            let previousImageData = null
            let additionalSuspicion = 0
            const frameTimestamps= []

            const analyzeFrame = () => {
                if (frameCount >= 60) {
                    // Aumentado a 60 frames para mejor análisis
                    const staticPercentage = (staticFrames / frameCount) * 100

                    // Análizar consistencia de timing entre frames
                    if (frameTimestamps.length > 10) {
                        const intervals = frameTimestamps.slice(1).map((time, i) => time - frameTimestamps[i])
                        const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length
                        const variance =
                            intervals.reduce((sum, interval) => sum + Math.pow(interval - avgInterval, 2), 0) / intervals.length

                        // Si el timing es demasiado perfecto, podría ser contenido pregrabado
                        if (variance < 1) {
                            additionalSuspicion += 20
                        }
                    }

                    resolve({ staticPercentage, additionalSuspicion })
                    return
                }

                frameTimestamps.push(performance.now())

                canvas.width = video.videoWidth
                canvas.height = video.videoHeight
                ctx.drawImage(video, 0, 0)

                const currentImageData = ctx.getImageData(0, 0, canvas.width, canvas.height)

                if (previousImageData) {
                    let differences = 0
                    const threshold = 10

                    for (let i = 0; i < currentImageData.data.length; i += 4) {
                        const rDiff = Math.abs(currentImageData.data[i] - previousImageData.data[i])
                        const gDiff = Math.abs(currentImageData.data[i + 1] - previousImageData.data[i + 1])
                        const bDiff = Math.abs(currentImageData.data[i + 2] - previousImageData.data[i + 2])

                        if (rDiff > threshold || gDiff > threshold || bDiff > threshold) {
                            differences++
                        }
                    }

                    const changePercentage = (differences / (currentImageData.data.length / 4)) * 100
                    if (changePercentage < 0.5) {
                        // Umbral más estricto
                        staticFrames++
                    }
                }

                previousImageData = currentImageData
                frameCount++

                requestAnimationFrame(analyzeFrame)
            }

            video.addEventListener("loadedmetadata", () => {
                analyzeFrame()
            })
        })
    }

    const performFullValidation = async (stream) => {
        return new Promise(async (resolve) => {

            try {
                const deviceValidation = await validateCamera(stream)
                const { staticPercentage, additionalSuspicion } = await analyzeVideoFrames(stream)

                if (staticPercentage > 85) {
                    deviceValidation.reasons.push("Contenido de video mayormente estático")
                    deviceValidation.confidence += 35
                    deviceValidation.isVirtual = true
                } else if (staticPercentage > 70) {
                    deviceValidation.reasons.push("Contenido de video con poca variación")
                    deviceValidation.confidence += 20
                }

                if (additionalSuspicion > 0) {
                    deviceValidation.reasons.push("Patrones de timing sospechosos en frames")
                    deviceValidation.confidence += additionalSuspicion
                }

                // Recalcular si es virtual basado en la confianza final
                deviceValidation.isVirtual = deviceValidation.confidence > 50

                resolve(deviceValidation)
            } catch (error) {
                console.error("Error in full validation:", error)
                resolve({
                    isVirtual: true,
                    confidence: 100,
                    reasons: ["Error durante la validación"],
                    deviceInfo: null,
                })
            }
        })
    }

    return {
        validationResult,
        isValidating,
        performFullValidation,
        videoRef,
        canvasRef,
    }
}
